import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { 
  ArrowLeft, 
  Building2, 
  FileText, 
  Clock, 
  DollarSign,
  Calendar,
  CreditCard,
  User,
  MessageSquare,
  Percent,
  CheckCircle,
  AlertCircle,
  Loader2,
  Upload,
  Send,
  Trash2,
  Download,
  X,
  File,
  XCircle,
  FileQuestion
} from 'lucide-react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import SuperAdminLayout from '@/components/superadmin/SuperAdminLayout';
import { extractErrorMessage } from '@/utils/errorHelpers';
import { STATUS_STYLES, ALL_STATUSES, getStatusStyle } from '@/lib/orderStatusStyles';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SuperAdminOrderDetail = () => {
  const { orderId } = useParams();
  const { token, user } = useAuth();
  
  const [order, setOrder] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Status update
  const [newStatus, setNewStatus] = useState('');
  const [statusUpdating, setStatusUpdating] = useState(false);
  
  // Discount
  const [discountType, setDiscountType] = useState('fixed');
  const [discountAmount, setDiscountAmount] = useState('');
  const [discountReason, setDiscountReason] = useState('');
  const [applyingDiscount, setApplyingDiscount] = useState(false);
  
  // Notes
  const [newNote, setNewNote] = useState('');
  const [addingNote, setAddingNote] = useState(false);
  
  // Payment confirmation
  const [confirmingPayment, setConfirmingPayment] = useState(false);

  const [successMessage, setSuccessMessage] = useState('');
  const [notes, setNotes] = useState([]);
  
  // Completed documents
  const [completedDocs, setCompletedDocs] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [showCompletionConfirm, setShowCompletionConfirm] = useState(false);
  const fileInputRef = useRef(null);
  
  // Action dialogs
  const [showApproveDialog, setShowApproveDialog] = useState(false);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [actionLoading, setActionLoading] = useState(null); // 'approve' | 'reject' | 'request_docs'
  
  // Document approve/reject dialogs
  const [showDocApproveDialog, setShowDocApproveDialog] = useState(false);
  const [showDocRejectDialog, setShowDocRejectDialog] = useState(false);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [docRejectReason, setDocRejectReason] = useState('');
  const [docActionLoading, setDocActionLoading] = useState(false);
  
  // Cancel order dialog (super admin only)
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);

  // Expiry date editor (RA/MF orders)
  const [expiryDateInput, setExpiryDateInput] = useState('');
  const [savingExpiry, setSavingExpiry] = useState(false);

  const isRaMfOrder = ['registered_agent', 'mail_forwarding'].includes(order?.service_type);

  const handleSaveExpiryDate = async () => {
    setSavingExpiry(true);
    setError('');
    try {
      const payload = { expiry_date: expiryDateInput ? new Date(expiryDateInput + 'T00:00:00+00:00').toISOString() : null };
      await axios.patch(`${API}/admin/orders/${orderId}/expiry-date`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuccessMessage(expiryDateInput ? 'Expiry date updated' : 'Expiry date cleared');
      setTimeout(() => setSuccessMessage(''), 3000);
      fetchOrder();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to update expiry date');
    } finally {
      setSavingExpiry(false);
    }
  };

  // Sync expiryDateInput when order loads
  useEffect(() => {
    if (order?.expiry_date) {
      setExpiryDateInput(order.expiry_date.slice(0, 10));
    } else {
      setExpiryDateInput('');
    }
  }, [order?.expiry_date]);

  const fetchOrder = async () => {
    setLoading(true);
    setError('');
    try {
      const [orderRes, submissionsRes, notesRes, docsRes] = await Promise.all([
        axios.get(`${API}/orders/${orderId}`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/orders/${orderId}/submissions`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: { submissions: [] } })),
        axios.get(`${API}/orders/${orderId}/notes`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: { notes: [] } })),
        axios.get(`${API}/orders/${orderId}/completed-documents`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: { documents: [] } }))
      ]);
      
      setOrder(orderRes.data);
      setNewStatus(orderRes.data.status);
      setSubmissions(submissionsRes.data.submissions || []);
      setNotes(notesRes.data.notes || notesRes.data || []);
      setCompletedDocs(docsRes.data.documents || []);
    } catch (err) {
      setError(extractErrorMessage(err, 'Failed to load order'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrder();
  }, [orderId, token]);

  // -----------------------------------------------------------------------
  // Variable-fee filing: admin confirms govt fee while order is pending_review
  // -----------------------------------------------------------------------
  const [govtFeeInput, setGovtFeeInput] = useState('');
  const [confirmingGovtFee, setConfirmingGovtFee] = useState(false);

  const parsedGovtFee = parseFloat(govtFeeInput) || 0;
  const govtFeePlatformFee = parseFloat(order?.platform_fee ?? order?.original_price ?? 100) || 0;
  const govtFeeTotal = (parsedGovtFee + govtFeePlatformFee).toFixed(2);

  const handleConfirmGovtFee = async () => {
    if (parsedGovtFee <= 0) {
      setError('Government fee must be greater than 0');
      return;
    }
    setConfirmingGovtFee(true);
    setError(null);
    try {
      await axios.patch(
        `${API}/admin/orders/${orderId}/confirm-govt-fee`,
        { govt_fee: parsedGovtFee },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setGovtFeeInput('');
      await fetchOrder();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to confirm govt fee');
    } finally {
      setConfirmingGovtFee(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatAmount = (amount) => {
    if (!amount && amount !== 0) return '-';
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
  };

  const showSuccess = (message) => {
    setSuccessMessage(message);
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  // Update order status - with confirmation for "completed"
  const handleStatusChange = (status) => {
    setNewStatus(status);
    if (status === 'completed' && order.status !== 'completed') {
      setShowCompletionConfirm(true);
    }
  };

  const handleStatusUpdate = async () => {
    if (!newStatus || newStatus === order.status) return;
    
    // If changing to completed, close confirmation dialog
    setShowCompletionConfirm(false);
    
    setStatusUpdating(true);
    try {
      // Backend route: PUT /api/admin/orders/{order_id}/status?status={status}
      await axios.put(`${API}/admin/orders/${orderId}/status?status=${encodeURIComponent(newStatus)}`, 
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showSuccess(newStatus === 'completed' ? 'Order completed! Client has been notified.' : 'Status updated successfully');
      fetchOrder();
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Failed to update status');
    } finally {
      setStatusUpdating(false);
    }
  };

  // Completed documents upload
  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    
    setUploading(true);
    setError('');
    
    for (const file of files) {
      // Validate file type and size
      const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
      if (!allowedTypes.includes(file.type)) {
        setError(`Invalid file type: ${file.name}. Allowed: PDF, JPG, PNG, DOCX`);
        continue;
      }
      if (file.size > 20 * 1024 * 1024) {
        setError(`File too large: ${file.name}. Max 20MB`);
        continue;
      }
      
      try {
        // Get presigned URL (admin endpoint)
        const presignedRes = await axios.post(`${API}/admin/upload/presigned`, {
          file_name: file.name,
          content_type: file.type,
          folder: `completed-docs/${orderId}`
        }, { headers: { Authorization: `Bearer ${token}` } });
        
        const { upload_url, file_url, s3_key } = presignedRes.data;
        
        // Upload to S3
        setUploadProgress(prev => ({ ...prev, [file.name]: 0 }));
        await axios.put(upload_url, file, {
          headers: { 'Content-Type': file.type },
          onUploadProgress: (progressEvent) => {
            const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(prev => ({ ...prev, [file.name]: percent }));
          }
        });
        
        // Prompt for document type
        const documentType = prompt(`Enter document type for "${file.name}":\n(e.g., Certificate of Formation, EIN Letter)`, 'Certificate of Formation');
        if (!documentType) continue;
        
        // Save to backend
        await axios.post(`${API}/admin/orders/${orderId}/completed-documents`, {
          file_name: file.name,
          file_url: file_url,
          s3_key: s3_key,
          document_type: documentType,
          file_size: file.size
        }, { headers: { Authorization: `Bearer ${token}` } });
        
        setUploadProgress(prev => {
          const newProgress = { ...prev };
          delete newProgress[file.name];
          return newProgress;
        });
        
      } catch (err) {
        console.error('Upload error:', err);
        setError(`Failed to upload ${file.name}`);
      }
    }
    
    setUploading(false);
    fetchOrder();
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleDeleteDoc = async (docId) => {
    if (!confirm('Delete this document?')) return;
    
    try {
      await axios.delete(`${API}/admin/orders/${orderId}/completed-documents/${docId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      showSuccess('Document deleted');
      fetchOrder();
    } catch (err) {
      setError('Failed to delete document');
    }
  };

  // Download completed document
  const handleDownloadDoc = async (docId, fileName) => {
    try {
      const response = await axios.get(`${API}/download/presigned/${docId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.download_url) {
        // Open download URL in new tab
        window.open(response.data.download_url, '_blank');
      }
    } catch (err) {
      console.error('Download error:', err);
      setError(`Failed to download ${fileName}`);
    }
  };

  // Download client submitted document (submission)
  const handleDownloadSubmission = async (submissionId, fileName) => {
    try {
      const response = await axios.get(`${API}/submissions/${submissionId}/download`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.download_url) {
        window.open(response.data.download_url, '_blank');
      }
    } catch (err) {
      console.error('Download submission error:', err);
      setError(`Failed to download ${fileName}`);
    }
  };

  // Apply discount
  const handleApplyDiscount = async () => {
    if (!discountAmount || !discountReason.trim()) {
      setError('Please enter discount amount and reason');
      return;
    }
    
    setApplyingDiscount(true);
    setError('');
    try {
      // Backend route: PUT /api/admin/orders/{order_id}/discount
      // Expects: { discount_percentage: float }
      // Note: Backend only supports percentage, so we convert fixed amount to percentage
      let discountPercentage = parseFloat(discountAmount);
      if (discountType === 'fixed' && order?.final_amount) {
        // Convert fixed amount to percentage of total
        discountPercentage = (parseFloat(discountAmount) / order.final_amount) * 100;
      }
      
      await axios.put(`${API}/admin/orders/${orderId}/discount`,
        { discount_percentage: discountPercentage },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showSuccess('Discount applied successfully');
      setDiscountAmount('');
      setDiscountReason('');
      fetchOrder();
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Failed to apply discount');
    } finally {
      setApplyingDiscount(false);
    }
  };

  // Confirm bank payment
  const handleConfirmPayment = async () => {
    if (!window.confirm('Confirm this bank transfer payment? This will mark the order as approved.')) return;
    
    setConfirmingPayment(true);
    setError('');
    try {
      await axios.put(`${API}/payments/${orderId}/confirm`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showSuccess('Payment confirmed successfully');
      fetchOrder();
    } catch (err) {
      setError(extractErrorMessage(err, 'Failed to confirm payment'));
    } finally {
      setConfirmingPayment(false);
    }
  };

  // Add note
  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    
    setAddingNote(true);
    try {
      // Backend route: POST /api/orders/{order_id}/notes
      // Expects: { order_id, note_text, is_internal, note_type }
      await axios.post(`${API}/orders/${orderId}/notes`,
        { 
          order_id: orderId,
          note_text: newNote,
          is_internal: true,
          note_type: 'general'
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showSuccess('Note added');
      setNewNote('');
      fetchOrder();
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (Array.isArray(detail)) {
        setError(detail[0]?.msg || 'Failed to add note');
      } else if (typeof detail === 'string') {
        setError(detail);
      } else {
        setError('Failed to add note');
      }
    } finally {
      setAddingNote(false);
    }
  };

  // Approve order
  const handleApproveOrder = async () => {
    setActionLoading('approve');
    setError('');
    try {
      await axios.put(`${API}/admin/orders/${orderId}/status?status=approved`, 
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showSuccess('Order approved! Client has been notified.');
      setShowApproveDialog(false);
      fetchOrder();
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Failed to approve order');
    } finally {
      setActionLoading(null);
    }
  };

  // Reject order
  const handleRejectOrder = async () => {
    if (!rejectReason.trim()) {
      setError('Please provide a reason for rejection');
      return;
    }
    
    setActionLoading('reject');
    setError('');
    try {
      // Update status to rejected
      await axios.put(`${API}/admin/orders/${orderId}/status?status=rejected`, 
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Add rejection reason as internal note
      await axios.post(`${API}/orders/${orderId}/notes`,
        { 
          order_id: orderId,
          note_text: `Order rejected: ${rejectReason}`,
          is_internal: true,
          note_type: 'rejection'
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      showSuccess('Order rejected. Client has been notified.');
      setShowRejectDialog(false);
      setRejectReason('');
      fetchOrder();
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Failed to reject order');
    } finally {
      setActionLoading(null);
    }
  };

  // Request documents
  const handleRequestDocuments = async () => {
    setActionLoading('request_docs');
    setError('');
    try {
      await axios.put(`${API}/admin/orders/${orderId}/status?status=documents_pending`, 
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showSuccess('Status updated to Documents Pending. Client can now upload documents.');
      fetchOrder();
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Failed to update status');
    } finally {
      setActionLoading(null);
    }
  };

  // Document Approval
  const handleDocApprove = async () => {
    if (!selectedSubmission) return;
    setDocActionLoading(true);
    setError('');
    try {
      await axios.post(`${API}/admin/orders/${orderId}/documents/approve`, 
        { submission_id: selectedSubmission.id },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showSuccess('Document approved successfully');
      setShowDocApproveDialog(false);
      setSelectedSubmission(null);
      fetchOrder();
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Failed to approve document');
    } finally {
      setDocActionLoading(false);
    }
  };

  // Document Rejection
  const handleDocReject = async () => {
    if (!selectedSubmission || !docRejectReason.trim()) {
      setError('Please provide a reason for rejection');
      return;
    }
    setDocActionLoading(true);
    setError('');
    try {
      await axios.post(`${API}/admin/orders/${orderId}/documents/reject`, 
        { submission_id: selectedSubmission.id, rejection_reason: docRejectReason.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showSuccess('Document rejected. Client has been notified.');
      setShowDocRejectDialog(false);
      setSelectedSubmission(null);
      setDocRejectReason('');
      fetchOrder();
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Failed to reject document');
    } finally {
      setDocActionLoading(false);
    }
  };

  // Handle cancel order (super admin only)
  const handleCancelOrder = async () => {
    try {
      setCancelLoading(true);
      await axios.put(
        `${API}/admin/orders/${orderId}/cancel`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setShowCancelDialog(false);
      setSuccessMessage('Order cancelled successfully');
      fetchOrder(); // Refresh
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to cancel order');
    } finally {
      setCancelLoading(false);
    }
  };

  // Button visibility helpers
  // Note: Valid statuses include draft, invoice_requested, payment_pending, payment_received, 
  // documents_pending, under_review, signature_pending, signature_review, completed, approved, rejected
  const canApprove = ['under_review', 'documents_pending', 'draft', 'payment_received'].includes(order?.status);
  const canReject = ['draft', 'pending_review', 'documents_pending', 'under_review', 'payment_pending'].includes(order?.status);
  const canRequestDocs = ['draft', 'payment_received', 'under_review'].includes(order?.status);

  if (loading) {
    return (
      <SuperAdminLayout>
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 text-[#00E87A] animate-spin" />
        </div>
      </SuperAdminLayout>
    );
  }

  if (error && !order) {
    return (
      <SuperAdminLayout>
        <div className="text-center py-12">
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <p className="text-red-400">{error}</p>
          <Link to="/superadmin/orders" className="text-[#00E87A] hover:underline mt-4 inline-block">
            Back to Orders
          </Link>
        </div>
      </SuperAdminLayout>
    );
  }

  const statusStyle = getStatusStyle(order?.status);
  const showBankPaymentConfirm = order?.payment_method === 'bank_transfer' && order?.status === 'payment_pending';

  return (
    <SuperAdminLayout>
      {/* Success Toast */}
      {successMessage && (
        <div className="fixed top-4 right-4 z-50 flex items-center gap-2 px-4 py-3 bg-[#00E87A] text-black rounded-lg shadow-lg">
          <CheckCircle className="w-5 h-5" />
          {successMessage}
        </div>
      )}

      {/* Back Link */}
      <Link
        to="/superadmin/orders"
        className="inline-flex items-center gap-2 text-gray-400 hover:text-white mb-6 transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to Orders
      </Link>

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white">{order?.service_name || order?.product_name || 'Order'}</h1>
          <p className="text-gray-400 text-sm mt-1">Order #{order?.id?.slice(0, 8)}</p>
        </div>
        <div className="flex items-center gap-3 mt-4 sm:mt-0">
          <span className={`inline-flex px-3 py-1.5 rounded text-sm font-medium ${statusStyle.bg} ${statusStyle.text}`}>
            {statusStyle.label}
          </span>
          {/* Cancel Order Button - Super Admin Only */}
          {user?.role === 'super_admin' && order?.status !== 'completed' && order?.status !== 'cancelled' && (
            <button
              onClick={() => setShowCancelDialog(true)}
              className="flex items-center gap-2 px-3 py-1.5 bg-red-500/20 text-red-400 rounded hover:bg-red-500/30 transition-colors text-sm font-medium"
              data-testid="cancel-order-btn"
            >
              <XCircle className="w-4 h-4" />
              Cancel Order
            </button>
          )}
        </div>
      </div>

      {/* Error message */}
      {error && (
        <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-lg flex items-center gap-2 text-red-400">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Govt Fee Confirmation Required (variable-fee state filings) */}
          {order?.status === 'pending_review' && (
            <section
              className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-6"
              data-testid="govt-fee-confirmation-panel"
            >
              <h2 className="text-lg font-semibold text-amber-300 flex items-center gap-2 mb-2">
                <AlertCircle className="w-5 h-5" />
                Government Fee Confirmation Required
              </h2>
              <p className="text-sm text-amber-200/80 mb-4">
                This is a variable-fee service. Set the exact government fee before the
                client can proceed to payment.
              </p>

              <div className="space-y-3 max-w-md">
                <div className="flex items-center justify-between gap-3">
                  <label className="text-sm text-gray-300">Government Fee</label>
                  <div className="flex items-center gap-1">
                    <span className="text-gray-400">$</span>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={govtFeeInput}
                      onChange={(e) => setGovtFeeInput(e.target.value)}
                      placeholder="0.00"
                      disabled={confirmingGovtFee}
                      className="w-32 px-3 py-1.5 bg-[#0D1117] border border-amber-500/40 rounded text-white text-right focus:outline-none focus:border-amber-400 disabled:opacity-50"
                      data-testid="govt-fee-input"
                    />
                  </div>
                </div>

                <div className="flex items-center justify-between gap-3 text-sm">
                  <span className="text-gray-400">Platform Fee</span>
                  <span className="font-mono text-gray-200">${govtFeePlatformFee.toFixed(2)}</span>
                </div>

                <div className="border-t border-amber-500/20 pt-2 flex items-center justify-between gap-3">
                  <span className="text-sm text-gray-300 font-medium">Total</span>
                  <span
                    className="font-mono font-bold text-amber-200"
                    data-testid="govt-fee-total"
                  >
                    ${govtFeeTotal}
                  </span>
                </div>

                <button
                  onClick={handleConfirmGovtFee}
                  disabled={confirmingGovtFee || parsedGovtFee <= 0}
                  className="w-full mt-2 px-4 py-2 rounded-md text-sm font-semibold text-gray-900 bg-[#00E87A] hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
                  data-testid="confirm-govt-fee-btn"
                >
                  {confirmingGovtFee ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                  {confirmingGovtFee ? 'Confirming…' : 'Confirm Fee & Notify Client'}
                </button>
              </div>
            </section>
          )}

          {/* Order Details */}
          <section className="bg-[#161B22] border border-gray-800 rounded-lg p-6">
            <h2 className="text-lg font-semibold text-white flex items-center gap-2 mb-4">
              <FileText className="w-5 h-5 text-[#00E87A]" />
              Order Details
            </h2>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-500">Service</p>
                <p className="text-white">{order?.service_name || order?.product_name || '-'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Company</p>
                {order?.company_id ? (
                  <Link 
                    to={`/superadmin/companies/${order.company_id}`}
                    className="text-[#00E87A] hover:text-[#00E87A]/80 transition-colors"
                    data-testid="company-link"
                  >
                    {order?.company_name || '-'}
                  </Link>
                ) : (
                  <p className="text-white">{order?.company_name || '-'}</p>
                )}
              </div>
              <div>
                <p className="text-sm text-gray-500">Client Email</p>
                <p className="text-white">{order?.user_email || '-'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Created</p>
                <p className="text-white">{formatDate(order?.created_at)}</p>
              </div>
            </div>
          </section>

          {/* Payment Section */}
          <section className="bg-[#161B22] border border-gray-800 rounded-lg p-6">
            <h2 className="text-lg font-semibold text-white flex items-center gap-2 mb-4">
              <DollarSign className="w-5 h-5 text-[#00E87A]" />
              Payment
            </h2>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
              <div>
                <p className="text-sm text-gray-500">Original Amount</p>
                <p className="text-white text-lg font-semibold">{formatAmount(order?.total_amount || order?.original_amount)}</p>
              </div>
              {order?.discount_amount > 0 && (
                <div>
                  <p className="text-sm text-gray-500">Discount</p>
                  <p className="text-[#00E87A] text-lg font-semibold">-{formatAmount(order?.discount_amount)}</p>
                </div>
              )}
              <div>
                <p className="text-sm text-gray-500">Final Amount</p>
                <p className="text-white text-xl font-bold">{formatAmount(order?.final_amount)}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Payment Method</p>
                <p className="text-white capitalize">{order?.payment_method?.replace('_', ' ') || '-'}</p>
              </div>
            </div>

            {/* Bank Payment Confirmation Panel */}
            {showBankPaymentConfirm && (
              <div className="mt-4 p-4 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
                <h3 className="text-yellow-400 font-medium mb-2 flex items-center gap-2">
                  <CreditCard className="w-5 h-5" />
                  Bank Transfer Pending Confirmation
                </h3>
                <p className="text-gray-400 text-sm mb-4">
                  This order was paid via bank transfer and awaits manual confirmation.
                </p>
                <button
                  onClick={handleConfirmPayment}
                  disabled={confirmingPayment}
                  className="px-6 py-3 rounded-lg font-medium text-white flex items-center gap-2 disabled:opacity-50"
                  style={{ backgroundColor: '#00E87A' }}
                  data-testid="confirm-payment-btn"
                >
                  {confirmingPayment ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <CheckCircle className="w-5 h-5" />
                  )}
                  Confirm Bank Payment
                </button>
              </div>
            )}
          </section>

          {/* Discount Panel - Super Admin Only */}
          <section className="bg-[#161B22] border border-gray-800 rounded-lg p-6">
            <h2 className="text-lg font-semibold text-white flex items-center gap-2 mb-4">
              <Percent className="w-5 h-5 text-[#F59E0B]" />
              Apply Discount
              <span className="text-xs px-2 py-0.5 rounded" style={{ backgroundColor: '#F59E0B20', color: '#F59E0B' }}>
                Super Admin
              </span>
            </h2>
            
            {order?.discount_amount > 0 && (
              <div className="mb-4 p-3 bg-[#00E87A]/10 border border-[#00E87A]/20 rounded-lg">
                <p className="text-[#00E87A] text-sm">
                  Discount already applied: {formatAmount(order?.discount_amount)}
                  {order?.discount_reason && <span className="text-gray-400"> - {order?.discount_reason}</span>}
                </p>
              </div>
            )}
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm text-gray-400 mb-2">Discount Type</label>
                <select
                  value={discountType}
                  onChange={(e) => setDiscountType(e.target.value)}
                  className="w-full px-4 py-2.5 bg-[#0D1117] border border-gray-700 rounded-lg text-white focus:outline-none focus:border-[#00E87A]"
                >
                  <option value="fixed">Fixed Amount ($)</option>
                  <option value="percentage">Percentage (%)</option>
                </select>
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-2">Amount</label>
                <input
                  type="number"
                  value={discountAmount}
                  onChange={(e) => setDiscountAmount(e.target.value)}
                  placeholder={discountType === 'fixed' ? '50.00' : '10'}
                  className="w-full px-4 py-2.5 bg-[#0D1117] border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#00E87A]"
                />
              </div>
            </div>
            
            <div className="mb-4">
              <label className="block text-sm text-gray-400 mb-2">
                Reason <span className="text-red-400">*</span>
              </label>
              <input
                type="text"
                value={discountReason}
                onChange={(e) => setDiscountReason(e.target.value)}
                placeholder="Enter reason for discount (required)"
                className="w-full px-4 py-2.5 bg-[#0D1117] border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#00E87A]"
              />
            </div>
            
            <button
              onClick={handleApplyDiscount}
              disabled={applyingDiscount || !discountAmount || !discountReason.trim()}
              className="px-5 py-2.5 rounded-lg font-medium text-black disabled:opacity-50 flex items-center gap-2"
              style={{ backgroundColor: '#F59E0B' }}
              data-testid="apply-discount-btn"
            >
              {applyingDiscount ? <Loader2 className="w-5 h-5 animate-spin" /> : <Percent className="w-5 h-5" />}
              Apply Discount
            </button>
          </section>

          {/* Client Submitted Documents */}
          <section className="bg-[#161B22] border border-gray-800 rounded-lg p-6">
            <h2 className="text-lg font-semibold text-white flex items-center gap-2 mb-2">
              <FileText className="w-5 h-5 text-blue-400" />
              Client Uploaded Documents
            </h2>
            <p className="text-gray-500 text-sm mb-4">
              Documents submitted by client during order placement. Review and approve/reject below.
            </p>
            
            {submissions.length > 0 ? (
              <div className="space-y-2">
                {submissions.map((sub) => (
                  <div key={sub.id} className="flex items-center justify-between p-3 bg-[#0D1117] border border-gray-700 rounded-lg">
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      <File className="w-5 h-5 text-blue-400 flex-shrink-0" />
                      <div className="min-w-0 flex-1">
                        <p className="text-white text-sm font-medium">{sub.document_type}</p>
                        <p className="text-gray-500 text-xs truncate">{sub.file_name}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <span className={`text-xs px-2 py-0.5 rounded-full ${
                            sub.status === 'approved' 
                              ? 'bg-green-500/20 text-green-400'
                              : sub.status === 'rejected'
                              ? 'bg-red-500/20 text-red-400'
                              : 'bg-yellow-500/20 text-yellow-400'
                          }`}>
                            {sub.status === 'approved' ? 'Approved' : sub.status === 'rejected' ? 'Rejected' : 'Pending Review'}
                          </span>
                          <span className="text-gray-600 text-xs">{formatDate(sub.uploaded_at)}</span>
                          {sub.status === 'rejected' && sub.rejection_reason && (
                            <span className="text-red-400 text-xs">Reason: {sub.rejection_reason}</span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      {/* Download button */}
                      <button
                        onClick={() => handleDownloadSubmission(sub.id, sub.file_name)}
                        className="p-2 text-blue-400 hover:text-blue-300 hover:bg-blue-500/10 rounded"
                        title="Download"
                        data-testid={`download-submission-${sub.id}`}
                      >
                        <Download className="w-4 h-4" />
                      </button>
                      {/* Approve/Reject buttons - only show for pending documents */}
                      {sub.status === 'pending' && (
                        <>
                          <button
                            onClick={() => {
                              setSelectedSubmission(sub);
                              setShowDocApproveDialog(true);
                            }}
                            className="p-2 text-green-400 hover:text-green-300 hover:bg-green-500/10 rounded"
                            title="Approve document"
                            data-testid={`approve-doc-${sub.id}`}
                          >
                            <CheckCircle className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => {
                              setSelectedSubmission(sub);
                              setShowDocRejectDialog(true);
                            }}
                            className="p-2 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded"
                            title="Reject document"
                            data-testid={`reject-doc-${sub.id}`}
                          >
                            <XCircle className="w-4 h-4" />
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-sm text-center py-2">No documents submitted by client</p>
            )}
          </section>

          {/* Completed Documents (Admin uploads for client delivery) */}
          <section className="bg-[#161B22] border border-gray-800 rounded-lg p-6">
            <h2 className="text-lg font-semibold text-white flex items-center gap-2 mb-2">
              <FileText className="w-5 h-5 text-[#00E87A]" />
              Completed Documents
            </h2>
            <p className="text-gray-500 text-sm mb-4">
              Upload documents to deliver to client (certificate, EIN letter etc)
            </p>
            
            {/* Upload Zone */}
            <div 
              onClick={() => fileInputRef.current?.click()}
              className="border-2 border-dashed border-gray-700 rounded-lg p-6 text-center cursor-pointer hover:border-gray-600 hover:bg-[#0D1117]/50 transition-colors mb-4"
            >
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept=".pdf,.jpg,.jpeg,.png,.docx"
                onChange={handleFileSelect}
                className="hidden"
              />
              <Upload className="w-8 h-8 text-gray-500 mx-auto mb-2" />
              <p className="text-gray-400 text-sm">
                {uploading ? 'Uploading...' : 'Click or drag files here'}
              </p>
              <p className="text-gray-600 text-xs mt-1">
                PDF, JPG, PNG, DOCX • Max 20MB
              </p>
            </div>
            
            {/* Upload Progress */}
            {Object.entries(uploadProgress).map(([fileName, progress]) => (
              <div key={fileName} className="mb-2">
                <div className="flex justify-between text-sm text-gray-400 mb-1">
                  <span className="truncate">{fileName}</span>
                  <span>{progress}%</span>
                </div>
                <div className="w-full bg-gray-700 rounded-full h-2">
                  <div 
                    className="bg-[#00E87A] h-2 rounded-full transition-all"
                    style={{ width: `${progress}%` }}
                  />
                </div>
              </div>
            ))}
            
            {/* Uploaded Documents List */}
            {completedDocs.length > 0 ? (
              <div className="space-y-2">
                {completedDocs.map((doc) => (
                  <div key={doc.id} className="flex items-center justify-between p-3 bg-[#0D1117] border border-gray-700 rounded-lg">
                    <div className="flex items-center gap-3 min-w-0">
                      <File className="w-5 h-5 text-[#00E87A] flex-shrink-0" />
                      <div className="min-w-0">
                        <p className="text-white text-sm font-medium truncate">{doc.document_type}</p>
                        <p className="text-gray-500 text-xs truncate">{doc.file_name}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <button
                        onClick={() => handleDownloadDoc(doc.id, doc.file_name)}
                        className="p-2 text-blue-400 hover:text-blue-300 hover:bg-blue-500/10 rounded"
                        title="Download"
                      >
                        <Download className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteDoc(doc.id)}
                        className="p-2 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-sm text-center py-2">No documents uploaded yet</p>
            )}

            {/* Mark Complete CTA — when approved with docs uploaded */}
            {order?.status === 'approved' && completedDocs.length > 0 && (
              <div className="mt-4 p-4 bg-[#00E87A]/10 border border-[#00E87A]/30 rounded-lg" data-testid="mark-complete-banner">
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-[#00E87A] mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="text-white font-medium text-sm">
                      {completedDocs.length} document{completedDocs.length > 1 ? 's' : ''} ready to deliver
                    </p>
                    <p className="text-gray-400 text-xs mt-1">
                      Mark this order complete to copy documents to the client's vault and send a notification email.
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setNewStatus('completed');
                    setShowCompletionConfirm(true);
                  }}
                  disabled={statusUpdating}
                  className="mt-3 w-full px-4 py-2.5 rounded-lg font-medium text-black bg-[#00E87A] hover:bg-[#00D46E] disabled:opacity-50 flex items-center justify-center gap-2 transition-colors"
                  data-testid="mark-complete-btn"
                >
                  {statusUpdating ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                  Mark Order Complete & Deliver to Client
                </button>
              </div>
            )}

            {/* Next-step hint — when approved but no docs yet */}
            {order?.status === 'approved' && completedDocs.length === 0 && (
              <div className="mt-4 p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-lg" data-testid="next-step-hint">
                <p className="text-yellow-400 text-sm">
                  Next step: upload final deliverables above, then mark this order complete to deliver them to the client.
                </p>
              </div>
            )}
          </section>

          {/* Notes */}
          <section className="bg-[#161B22] border border-gray-800 rounded-lg p-6">
            <h2 className="text-lg font-semibold text-white flex items-center gap-2 mb-4">
              <MessageSquare className="w-5 h-5 text-[#00E87A]" />
              Internal Notes
            </h2>
            
            <div className="flex gap-2 mb-4">
              <input
                type="text"
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
                placeholder="Add a note..."
                className="flex-1 px-4 py-2.5 bg-[#0D1117] border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#00E87A]"
              />
              <button
                onClick={handleAddNote}
                disabled={addingNote || !newNote.trim()}
                className="px-4 py-2.5 rounded-lg font-medium text-white disabled:opacity-50"
                style={{ backgroundColor: '#00E87A' }}
              >
                {addingNote ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
              </button>
            </div>
            
            {notes && notes.length > 0 ? (
              <div className="space-y-2">
                {notes.map((note, index) => (
                  <div key={note.id || index} className="p-3 bg-[#0D1117] rounded-lg">
                    <p className="text-white text-sm">{note.note_text}</p>
                    <p className="text-gray-500 text-xs mt-1">{formatDate(note.created_at)} by {note.created_by_email || 'Admin'}</p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-sm">No notes yet</p>
            )}
          </section>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Quick Actions */}
          {(canApprove || canReject || canRequestDocs) && (
            <section className="bg-[#161B22] border border-gray-800 rounded-lg p-6">
              <h2 className="text-lg font-semibold text-white mb-4">Quick Actions</h2>
              
              <div className="space-y-3">
                {/* Approve Button */}
                {canApprove && (
                  <button
                    onClick={() => setShowApproveDialog(true)}
                    disabled={actionLoading === 'approve'}
                    className="w-full px-4 py-3 rounded-lg font-medium text-white bg-[#00E87A] hover:bg-[#00D46E] disabled:opacity-50 flex items-center justify-center gap-2 transition-colors"
                    data-testid="approve-order-btn"
                  >
                    {actionLoading === 'approve' ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <CheckCircle className="w-5 h-5" />
                    )}
                    Approve Order
                  </button>
                )}
                
                {/* Request Documents Button */}
                {canRequestDocs && (
                  <button
                    onClick={handleRequestDocuments}
                    disabled={actionLoading === 'request_docs'}
                    className="w-full px-4 py-3 rounded-lg font-medium text-white bg-gray-700 hover:bg-gray-600 disabled:opacity-50 flex items-center justify-center gap-2 transition-colors"
                    data-testid="request-docs-btn"
                  >
                    {actionLoading === 'request_docs' ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <FileQuestion className="w-5 h-5" />
                    )}
                    Request Documents
                  </button>
                )}
                
                {/* Reject Button */}
                {canReject && (
                  <button
                    onClick={() => setShowRejectDialog(true)}
                    disabled={actionLoading === 'reject'}
                    className="w-full px-4 py-3 rounded-lg font-medium text-white bg-red-600 hover:bg-red-500 disabled:opacity-50 flex items-center justify-center gap-2 transition-colors"
                    data-testid="reject-order-btn"
                  >
                    {actionLoading === 'reject' ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <XCircle className="w-5 h-5" />
                    )}
                    Reject Order
                  </button>
                )}
              </div>
            </section>
          )}

          {/* Status Control */}
          <section className="bg-[#161B22] border border-gray-800 rounded-lg p-6">
            <h2 className="text-lg font-semibold text-white mb-4">Update Status</h2>
            
            <select
              value={newStatus}
              onChange={(e) => handleStatusChange(e.target.value)}
              className="w-full px-4 py-2.5 bg-[#0D1117] border border-gray-700 rounded-lg text-white focus:outline-none focus:border-[#00E87A] mb-4"
              data-testid="status-select"
            >
              {ALL_STATUSES.map(status => (
                <option key={status} value={status}>
                  {STATUS_STYLES[status].label}
                </option>
              ))}
            </select>
            
            <button
              onClick={handleStatusUpdate}
              disabled={statusUpdating || newStatus === order?.status}
              className="w-full px-4 py-2.5 rounded-lg font-medium text-white disabled:opacity-50"
              style={{ backgroundColor: '#00E87A' }}
              data-testid="update-status-btn"
            >
              {statusUpdating ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : 'Update Status'}
            </button>
          </section>

          {/* Expiry Date Editor — RA/MF orders only */}
          {isRaMfOrder && (
            <section className="bg-[#161B22] border border-gray-800 rounded-lg p-6" data-testid="expiry-date-section">
              <h2 className="text-lg font-semibold text-white flex items-center gap-2 mb-4">
                <Calendar className="w-5 h-5 text-[#F59E0B]" />
                Expiry Date
              </h2>

              {order?.expiry_date ? (
                <p className="text-sm text-gray-400 mb-3">
                  Current: <span className="text-white font-medium">{formatDate(order.expiry_date)?.split(',')[0] || order.expiry_date.slice(0, 10)}</span>
                </p>
              ) : (
                <p className="text-sm text-yellow-400 mb-3">No expiry date set</p>
              )}

              <input
                type="date"
                value={expiryDateInput}
                onChange={(e) => setExpiryDateInput(e.target.value)}
                className="w-full px-4 py-2.5 bg-[#0D1117] border border-gray-700 rounded-lg text-white focus:outline-none focus:border-[#00E87A] mb-3"
                data-testid="expiry-date-input"
              />

              <div className="flex gap-2">
                <button
                  onClick={handleSaveExpiryDate}
                  disabled={savingExpiry}
                  className="flex-1 px-4 py-2.5 rounded-lg font-medium text-white disabled:opacity-50 flex items-center justify-center gap-2"
                  style={{ backgroundColor: '#00E87A' }}
                  data-testid="save-expiry-btn"
                >
                  {savingExpiry ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                  {expiryDateInput ? 'Save' : 'Clear'}
                </button>
                {order?.expiry_date && (
                  <button
                    onClick={() => { setExpiryDateInput(''); }}
                    className="px-4 py-2.5 rounded-lg font-medium text-red-400 bg-red-500/10 hover:bg-red-500/20 transition-colors"
                    data-testid="clear-expiry-btn"
                  >
                    Clear
                  </button>
                )}
              </div>
            </section>
          )}

          {/* Quick Info */}
          <section className="bg-[#161B22] border border-gray-800 rounded-lg p-6">
            <h2 className="text-lg font-semibold text-white mb-4">Quick Info</h2>
            
            <div className="space-y-3 text-sm">
              <div className="flex items-center gap-2">
                <User className="w-4 h-4 text-gray-500" />
                <span className="text-gray-400">Client:</span>
                <span className="text-white truncate">{order?.user_email}</span>
              </div>
              <div className="flex items-center gap-2">
                <Building2 className="w-4 h-4 text-gray-500" />
                <span className="text-gray-400">Company:</span>
                {order?.company_id ? (
                  <Link 
                    to={`/superadmin/companies/${order.company_id}`}
                    className="text-[#00E87A] hover:text-[#00E87A]/80 transition-colors"
                  >
                    {order?.company_name || '-'}
                  </Link>
                ) : (
                  <span className="text-white">{order?.company_name || '-'}</span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-gray-500" />
                <span className="text-gray-400">Created:</span>
                <span className="text-white">{formatDate(order?.created_at)?.split(',')[0]}</span>
              </div>
              <div className="flex items-center gap-2">
                <CreditCard className="w-4 h-4 text-gray-500" />
                <span className="text-gray-400">Payment:</span>
                <span className="text-white capitalize">{order?.payment_method?.replace('_', ' ') || '-'}</span>
              </div>
            </div>
          </section>
        </div>
      </div>

      {/* Completion Confirmation Modal */}
      {showCompletionConfirm && (
        <>
          <div className="fixed inset-0 bg-black/50 z-50" onClick={() => setShowCompletionConfirm(false)} />
          <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
            <div className="bg-[#161B22] border border-gray-800 rounded-lg p-6 max-w-md w-full">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-[#00E87A]/20 rounded-full">
                  <CheckCircle className="w-6 h-6 text-[#00E87A]" />
                </div>
                <h3 className="text-lg font-semibold text-white">Mark as Complete?</h3>
              </div>
              
              <p className="text-gray-400 mb-6">
                Marking as complete will notify the client that their documents are ready to download.
                {completedDocs.length === 0 && (
                  <span className="block mt-2 text-yellow-500 text-sm">
                    Warning: No documents have been uploaded yet.
                  </span>
                )}
              </p>
              
              <div className="flex gap-3">
                <button
                  onClick={() => setShowCompletionConfirm(false)}
                  className="flex-1 px-4 py-2.5 rounded-lg font-medium text-white bg-gray-700 hover:bg-gray-600"
                >
                  Cancel
                </button>
                <button
                  onClick={handleStatusUpdate}
                  disabled={statusUpdating}
                  className="flex-1 px-4 py-2.5 rounded-lg font-medium text-black bg-[#00E87A] hover:bg-[#00D46E] disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {statusUpdating ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                  Confirm & Notify Client
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Approve Order Confirmation Dialog */}
      {showApproveDialog && (
        <>
          <div className="fixed inset-0 bg-black/50 z-50" onClick={() => setShowApproveDialog(false)} />
          <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
            <div className="bg-[#161B22] border border-gray-800 rounded-lg p-6 max-w-md w-full">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-[#00E87A]/20 rounded-full">
                  <CheckCircle className="w-6 h-6 text-[#00E87A]" />
                </div>
                <h3 className="text-lg font-semibold text-white">Approve Order?</h3>
              </div>
              
              <p className="text-gray-400 mb-6">
                Approve this order and notify the client? This action confirms the order is accepted and will proceed.
              </p>
              
              <div className="flex gap-3">
                <button
                  onClick={() => setShowApproveDialog(false)}
                  className="flex-1 px-4 py-2.5 rounded-lg font-medium text-white bg-gray-700 hover:bg-gray-600"
                >
                  Cancel
                </button>
                <button
                  onClick={handleApproveOrder}
                  disabled={actionLoading === 'approve'}
                  className="flex-1 px-4 py-2.5 rounded-lg font-medium text-black bg-[#00E87A] hover:bg-[#00D46E] disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="confirm-approve-btn"
                >
                  {actionLoading === 'approve' ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                  Approve Order
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Reject Order Dialog */}
      {showRejectDialog && (
        <>
          <div className="fixed inset-0 bg-black/50 z-50" onClick={() => setShowRejectDialog(false)} />
          <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
            <div className="bg-[#161B22] border border-gray-800 rounded-lg p-6 max-w-md w-full">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-red-500/20 rounded-full">
                  <XCircle className="w-6 h-6 text-red-400" />
                </div>
                <h3 className="text-lg font-semibold text-white">Reject Order?</h3>
              </div>
              
              <p className="text-gray-400 mb-4">
                Please provide a reason for rejecting this order. The client will be notified.
              </p>
              
              <div className="mb-6">
                <label className="block text-sm text-gray-400 mb-2">
                  Rejection Reason <span className="text-red-400">*</span>
                </label>
                <textarea
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  placeholder="Enter the reason for rejection..."
                  rows={3}
                  className="w-full px-4 py-2.5 bg-[#0D1117] border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-red-400 resize-none"
                  data-testid="reject-reason-input"
                />
              </div>
              
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowRejectDialog(false);
                    setRejectReason('');
                  }}
                  className="flex-1 px-4 py-2.5 rounded-lg font-medium text-white bg-gray-700 hover:bg-gray-600"
                >
                  Cancel
                </button>
                <button
                  onClick={handleRejectOrder}
                  disabled={actionLoading === 'reject' || !rejectReason.trim()}
                  className="flex-1 px-4 py-2.5 rounded-lg font-medium text-white bg-red-600 hover:bg-red-500 disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="confirm-reject-btn"
                >
                  {actionLoading === 'reject' ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                  Reject Order
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Document Approve Confirmation Dialog */}
      {showDocApproveDialog && selectedSubmission && (
        <>
          <div className="fixed inset-0 bg-black/50 z-50" onClick={() => setShowDocApproveDialog(false)} />
          <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
            <div className="bg-[#161B22] border border-gray-800 rounded-lg p-6 max-w-md w-full">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-[#00E87A]/20 rounded-full">
                  <CheckCircle className="w-6 h-6 text-[#00E87A]" />
                </div>
                <h3 className="text-lg font-semibold text-white">Approve Document?</h3>
              </div>
              
              <p className="text-gray-400 mb-2">
                Are you sure you want to approve this document?
              </p>
              <div className="p-3 bg-[#0D1117] rounded-lg mb-6">
                <p className="text-white font-medium">{selectedSubmission.document_type}</p>
                <p className="text-gray-500 text-sm">{selectedSubmission.file_name}</p>
              </div>
              
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowDocApproveDialog(false);
                    setSelectedSubmission(null);
                  }}
                  className="flex-1 px-4 py-2.5 rounded-lg font-medium text-white bg-gray-700 hover:bg-gray-600"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDocApprove}
                  disabled={docActionLoading}
                  className="flex-1 px-4 py-2.5 rounded-lg font-medium text-black bg-[#00E87A] hover:bg-[#00D46E] disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="confirm-doc-approve-btn"
                >
                  {docActionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                  Approve Document
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Document Reject Dialog */}
      {showDocRejectDialog && selectedSubmission && (
        <>
          <div className="fixed inset-0 bg-black/50 z-50" onClick={() => setShowDocRejectDialog(false)} />
          <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
            <div className="bg-[#161B22] border border-gray-800 rounded-lg p-6 max-w-md w-full">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-red-500/20 rounded-full">
                  <XCircle className="w-6 h-6 text-red-400" />
                </div>
                <h3 className="text-lg font-semibold text-white">Reject Document?</h3>
              </div>
              
              <p className="text-gray-400 mb-2">
                Please provide a reason for rejection. The client will be notified and asked to reupload.
              </p>
              <div className="p-3 bg-[#0D1117] rounded-lg mb-4">
                <p className="text-white font-medium">{selectedSubmission.document_type}</p>
                <p className="text-gray-500 text-sm">{selectedSubmission.file_name}</p>
              </div>
              
              <div className="mb-6">
                <label className="block text-sm text-gray-400 mb-2">
                  Rejection Reason <span className="text-red-400">*</span>
                </label>
                <textarea
                  value={docRejectReason}
                  onChange={(e) => setDocRejectReason(e.target.value)}
                  placeholder="e.g., Document is blurry, please reupload a clearer copy"
                  rows={3}
                  className="w-full px-4 py-2.5 bg-[#0D1117] border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-red-400 resize-none"
                  data-testid="doc-reject-reason-input"
                />
              </div>
              
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowDocRejectDialog(false);
                    setSelectedSubmission(null);
                    setDocRejectReason('');
                  }}
                  className="flex-1 px-4 py-2.5 rounded-lg font-medium text-white bg-gray-700 hover:bg-gray-600"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDocReject}
                  disabled={docActionLoading || !docRejectReason.trim()}
                  className="flex-1 px-4 py-2.5 rounded-lg font-medium text-white bg-red-600 hover:bg-red-500 disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="confirm-doc-reject-btn"
                >
                  {docActionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                  Reject Document
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Cancel Order Confirmation Modal */}
      {showCancelDialog && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
          <div className="bg-[#161B22] border border-gray-700 rounded-xl p-6 max-w-md w-full mx-4" data-testid="cancel-order-modal">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-500/20 flex items-center justify-center">
                <XCircle className="w-5 h-5 text-red-400" />
              </div>
              <h3 className="text-lg font-semibold text-white">Cancel Order</h3>
            </div>
            <p className="text-gray-400 mb-6">
              Are you sure you want to cancel this order? This action cannot be undone. 
              The order will be marked as cancelled and no further processing will occur.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowCancelDialog(false)}
                className="px-4 py-2 text-gray-400 hover:text-white transition-colors"
                data-testid="cancel-dialog-dismiss"
              >
                Go Back
              </button>
              <button
                onClick={handleCancelOrder}
                disabled={cancelLoading}
                className="flex items-center gap-2 px-4 py-2 bg-red-500 text-white font-medium rounded-lg hover:bg-red-600 transition-colors disabled:opacity-50"
                data-testid="confirm-cancel-order-btn"
              >
                {cancelLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <XCircle className="w-4 h-4" />}
                Cancel Order
              </button>
            </div>
          </div>
        </div>
      )}
    </SuperAdminLayout>
  );
};

export default SuperAdminOrderDetail;
